import { Eyebrow, LinkButton, Pill } from '@/components/ui';
import { ArrowRightIcon, LockIcon, ShieldIcon, SyncIcon, WatchIcon } from '@/lib/icons';

const RULES = [
  'A lifter has at most one human coach at a time — mirroring the single AI coach. Adding a second requires removing the first.',
  'A coach can hold as many clients as their plan’s seat limit allows.',
  'Sharing is scoped and revocable by the lifter at any time; revoking is instant and auditable.',
  'A coach sees a client’s data only while the link is active, and only the fields the lifter shares.',
  'The relationship is the same record whether it was created from the lifter side or the CRM side.',
];

/**
 * The differentiator: the client is not trapped inside the coach’s tool. They
 * get a full lifter product of their own, and the coach gets a scoped,
 * revocable window into it (platform plan §1 and §3b).
 */
export function CrmRelationship() {
  return (
    <section id="crm-clients" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="flex flex-col gap-5">
            <Eyebrow>The differentiator</Eyebrow>
            <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
              Your clients get a real app, not a portal.
            </h2>
            <p className="max-w-[68ch] text-body-lg text-ink-muted">
              Every client you add gets a full lifter product of their own — the log, the programme, goals and
              the AI coach — on web, phone and watch. They can bring their coach to it, or you can bring them in
              by invite. Nothing about them lives only inside your workspace.
            </p>

            <ul className="flex flex-wrap gap-2">
              {['Web', 'iOS', 'Android', 'Apple Watch'].map((platform) => (
                <li
                  key={platform}
                  className="rounded-pill border border-hairline bg-surface px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink"
                >
                  {platform}
                </li>
              ))}
            </ul>

            <div className="rounded-card border border-accent/40 bg-accent/10 p-6">
              <h3 className="mb-2 font-display text-[18px] font-black uppercase tracking-[-0.01em] text-accent">
                Two ends of one relationship.
              </h3>
              <p className="mb-5 text-[13px] leading-relaxed text-ink">
                The nearest competitor is a tracker that added a coach tool; Lift2Fit is a platform where the
                lifter and the coach are two ends of one relationship. That is why the seat rules, the invite and
                the programme authority are the same on both sides.
              </p>
              <LinkButton href="#crm-pricing" size="md" className="w-full">
                See how many clients your tier covers <ArrowRightIcon className="text-sm" />
              </LinkButton>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-7 shadow-card">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-display text-[18px] font-black uppercase tracking-[-0.01em] text-ink">
                How the link works
              </h3>
              <Pill tone="accent">
                <ShieldIcon className="text-xs" /> Scoped
              </Pill>
            </div>
            <ul className="flex flex-col gap-3.5">
              {RULES.map((rule) => (
                <li key={rule} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-ink">
                  <LockIcon className="mt-[2px] shrink-0 text-sm text-accent" />
                  {rule}
                </li>
              ))}
            </ul>
            <p className="mt-2 flex items-center gap-2 border-t border-hairline pt-4 text-[12px] text-ink-muted">
              <SyncIcon className="shrink-0 text-accent" />
              One database, one API, one relationship model. A change on either side lands on both.
            </p>
            <p className="flex items-center gap-2 text-[12px] text-ink-muted">
              <WatchIcon className="shrink-0 text-accent" />
              The CRM is web only; your clients’ lifting is not.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
